'use client';

import React from 'react';
import Link from 'next/link';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { ArrayElementType } from '@/integrations/crystallize/types/helpers/ArrayElementType';
import { TileItemRelationsFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { buildTrackingId } from '@/utils/tracking-helper';

export default function ProductCard({
  product,
  trackingPrefix,
}: Readonly<{
  product: ArrayElementType<TileItemRelationsFragment['items']>;
  trackingPrefix?: string;
}>) {
  const { localizedLink } = useLocaleLink();
  const { formatCurrency } = useCurrencyFormatter();

  const productContent = product.__typename === 'Product' ? product : undefined;
  const defaultVariant = productContent?.variants?.find((variant) => variant.isDefault);
  const priceVariant = defaultVariant?.priceVariant;
  const shortDescription = componentByTypename(
    productContent?.shortDescription?.content,
    'RichTextContent',
  );
  const productName = productContent?.name;
  const productPath = productContent?.path;
  const productImage = defaultVariant?.images?.[0];
  const productCardTrackingId = buildTrackingId(trackingPrefix, productName);

  return (
    <Link
      href={localizedLink(productPath || '/')}
      className="group w-full transform overflow-hidden rounded-3xl"
      style={{
        boxShadow: '0px 0px 1px 0px rgba(33, 37, 41, 0.32), 0px 4px 16px 0px rgba(0, 0, 0, 0.1)',
      }}
      data-tile-element-id={productCardTrackingId}
    >
      <div className=" relative z-0 h-37 py-4 sm:h-50">
        <div className=" absolute bottom-0 left-0 right-0 -z-10 h-1/4 bg-gradient-to-t from-neutral-5 to-transparent opacity-30" />
        <OptimizedImage
          image={productImage}
          width={300}
          height={300}
          className=" z-10 h-full w-full object-contain transition-transform duration-300 group-hover:scale-110"
          sizes="(max-width: 640px) 90vw, 33vw"
        />
      </div>
      <div className="space-y-1 p-4">
        <h3 className=" font-bold sm:text-md">{productName}</h3>
        <RichTextRenderer blocks={shortDescription} className="text-sm text-neutral-60 sm:w-4/5" />
        {priceVariant && (
          <span className=" block pt-1 font-bold sm:text-md">
            {formatCurrency(priceVariant, {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            })}
          </span>
        )}
      </div>
    </Link>
  );
}
